import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { VscAzureDevops } from "react-icons/vsc";

import { PipelineMappingsSkeleton } from "@/azure-devops/components/pipeline-mappings-skeleton";
import { PipelineMappingsView } from "@/azure-devops/components/pipeline-mappings-view";
import { useTRPC } from "@/infrastructure/trpc/react";
import { Button } from "@/shared/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/shared/ui/empty";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";

export const Route = createFileRoute(
  "/app/organization/$id/settings/azure-devops-mappings"
)({
  loader: async ({ context, params }) => {
    await context.queryClient
      .query(
        context.trpc.azureDevOps.list.queryOptions({
          organizationId: params.id,
        })
      )
      .catch(() => undefined);
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();

  return (
    <Suspense fallback={<PipelineMappingsSkeleton />}>
      <AzureDevOpsMappingsContent organizationId={id} />
    </Suspense>
  );
}

function AzureDevOpsMappingsContent({
  organizationId,
}: {
  organizationId: string;
}) {
  const trpc = useTRPC();
  const { data } = useSuspenseQuery(
    trpc.azureDevOps.list.queryOptions({
      organizationId,
    })
  );

  const [selectedConnectionId, setSelectedConnectionId] =
    useState<string | undefined>(data.connections[0]?.id);

  // No connections
  if (data.connections.length === 0) {
    return (
      <Empty variant="taller">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <VscAzureDevops className="size-5" />
          </EmptyMedia>
          <EmptyTitle>
            No Azure DevOps Connections
          </EmptyTitle>
          <EmptyDescription>
            Add an Azure DevOps connection to see pipeline
            repository mappings.
          </EmptyDescription>
        </EmptyHeader>
        <Button
          render={
            <Link
              to="/app/organization/$id/settings/azure-devops"
              params={{ id: organizationId }}
            />
          }
          variant="outline"
          size="sm"
        >
          <VscAzureDevops className="mr-2 size-4" />
          Go to Azure DevOps Settings
        </Button>
      </Empty>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Selector */}
      {data.connections.length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium">
            Connection:
          </label>
          <Select
            value={selectedConnectionId}
            onValueChange={(value) =>
              value && setSelectedConnectionId(value)
            }
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select a connection" />
            </SelectTrigger>
            <SelectContent>
              {data.connections.map((conn) => (
                <SelectItem key={conn.id} value={conn.id}>
                  {conn.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Mappings View */}
      {selectedConnectionId && (
        <Suspense fallback={<PipelineMappingsSkeleton />}>
          <PipelineMappingsView
            key={selectedConnectionId}
            organizationId={organizationId}
            connectionId={selectedConnectionId}
          />
        </Suspense>
      )}
    </div>
  );
}
